<template>
  <div :class="grid_item_classes" @mouseover="$emit('hover', box)" @mouseleave="$emit('blur', box)">
    <div :class="grid_item_wrapper_classes">
      <div class="grid-item__content">
        <slot name="icon">
          <div class="grid-item__icon" :style="grid_item_icon_styles"></div>
        </slot>
      </div>
    </div>

    <actions-menu
      :is_active="is_menu_open(box.id)"
      :is_allow_config="is_allow_config"
      :is_allow_create_connection="is_allow_create_connection"
      class="grid-item__menu"
      @button-remove="remove"
      @button-config="config"
      @button-connection="$emit('button-connection')"
    />

    <div class="grid-item__title">
      <slot name="title">{{ name }}</slot>
    </div>
    <slot></slot>
  </div>
</template>

<script>
  import { WorksheetItemService } from '@pbx/services/WorksheetItemService';

  import { BasePropsMixin } from './_base-mixin';
  import ActionsMenu from './_menu';
  import { WorksheetActionModalService } from '@pbx/modals/WorksheetActionModalService';
  import { mapActions, mapGetters } from 'vuex';
  import { isID } from '@shared/helpers/uid';
  import isNull from 'lodash-es/isNull';

  import { WorksheetActionModelSelector } from '@pbx/helpers/worksheets/action-types';

  export default {
    components: { ActionsMenu },
    mixins: [BasePropsMixin],

    /**
     * TODO :: Привести к единому виду
     **/
    computed: {
      ...mapGetters('pbx/worksheets/form', {
        worksheet_id: 'getId',
        is_menu_open: 'isMenuOpen',
        get_connection_id_from: 'getConnectionIdFrom',
        is_allow_any_connection: 'isAllowAnyConnection',
        items: 'getActions',
      }),

      grid_item_classes: function() {
        return {
          'grid-item': true,
          'grid-item--active': this.is_active,
          [`grid-item--${this.box.type}`]: true
        };
      },

      grid_item_wrapper_classes: function() {
        return {
          'grid-item__wrapper': true,
          'grid-item__wrapper--active': this.is_active,
          [`grid-item__wrapper--${this.box.type}`]: true
        };
      },

      grid_item_icon_styles: function() {
        return {
          'background-image': `url(/img/icons/pbx/action-types/${this.box.type}.svg)`
        };
      },

      name: function() {
        return this.box.getName();
      },

      is_allow_config: function() {
        const component = WorksheetActionModalService.get_config_modal_by_type(this.box.type);
        return !isNull(component);
      },

      is_allow_create_connection: function() {
        return this.box.hasFreeOptions();
      },

      is_active: function() {
        if (!this.get_connection_id_from) {
          return false;
        }

        return this.is_allow_any_connection(this.get_connection_id_from, this.box.id);
      }
    },

    mounted() {
      /**
       * Если объект имеет не числовую id, а временную - значит его только добавили.
       * Значит нужно запустить процесс настройки
       */
      if (!isID(this.box.id)) {
        if (this.is_allow_config) {
          this.config();
        }
      }
    },

    methods: {
      ...mapActions('pbx/worksheets/form', { save_item: 'saveItem', remove_item: 'removeItem' }),

      remove: function() {
        WorksheetActionModalService.remove(
          this.worksheet_id,
          this.box.id,
          {
            name: this.name,
            type: this.box.type
          },
          this.$el
        )
          .on('success', item => {            
            this.remove_item(item);
            this.update_actions(this.items?.filter(action => action.id !== item.id));
          })
          .on('error', e => this.$noty.server_error(e));
      },

      update_actions: function(itemsWithoutDeleted) {
        itemsWithoutDeleted.length > 0 && itemsWithoutDeleted?.forEach(item => {

          if(Number.isInteger(item.id)) {
            const backend_item = WorksheetActionModelSelector.getFrontendModel(item).toBackend();
            WorksheetItemService.getItem(
              backend_item.type,
              this.worksheet_id,
              backend_item.id,
            ).then((response) => {
              this.save_item({
                id: backend_item.id,
                item: response
              });
            })
          }

        });
      },

      config() {
        WorksheetActionModalService.config(this.worksheet_id, this.box, this.$el)
          .on('success', item => {
            this.save_item({
              id: this.box.id,
              item: item
            });
          })
          .on('cancel', () => {
            /**
             * Если это новый блок и его не настроили (закрыли модалку настройки),
             * то удаляем этот блок
             */
            if (!isID(this.box.id)) {
              this.remove_item({ id: this.box.id });
            }
          })
          .on('error', e => this.$noty.server_error(e));
      }
    }
  };
</script>

<style lang="scss">
  @keyframes spinner {
    to {
      transform: rotate(360deg);
    }
  }

  .grid-item {
    border-radius: 100%;
    width: 100%;
    height: 100%;
    position: relative;
    user-select: none;

    &__wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      border-radius: 100%;
      background: $grey;

      &:before {
        content: '';
        box-sizing: border-box;
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 4px solid #ccc;
      }

      @each $type, $color in $pbx-action-types {
        &--#{$type} {
          &:before {
            border-color: $color;
          }
        }
      }

      &--active {
        &:before {
          border: 4px solid #ccc;
          border-top-color: #07d;
          animation: spinner 1s linear infinite;
        }

        @each $type, $color in $pbx-action-types {
          &.grid-item__wrapper--#{$type} {
            &:before {
              border-top-color: $color;
            }
          }
        }
      }
    }

    &__content {
      z-index: 3;
      background: $white;
      width: 60px;
      height: 60px;
      border-radius: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15), 4px 4px 4px 0 rgba(0, 0, 0, 0.15);
    }

    &__title {
      text-align: center;
      margin-top: 10px;
      color: $header-color;
      font-size: 14px;
      width: 200%;
      transform: translateX(-25%);
      background: rgba(255, 255, 255, 0.8);
      padding: 4px 8px;
      border-radius: 4px;
      word-wrap: break-word;
      font-weight: 700;
    }

    &__icon {
      width: 100%;
      height: 100%;
      background-repeat: no-repeat;
      background-position: center;
      border-radius: 100%;
    }

    &__menu {
      position: absolute;
      right: 50%;
      top: 0;
    }
  }
</style>
